import { Box, Button, Card, CardContent, CardHeader, FormHelperText, Grid, Stack, Drawer, Tooltip, Typography, useMediaQuery } from "@mui/material";
import { useFormik } from "formik";
import CheckIcon from '@mui/icons-material/Check';
import ClearIcon from '@mui/icons-material/Clear';
import { useRecordContext } from "ra-core";
import { FC, useCallback, useEffect } from "react";
import { Helmet } from 'react-helmet-async';
import * as Yup from 'yup'
import OdForm, { OnSubmit } from "@/components/formik/OdForm";
import TextField from "@/components/formik/TextField";
import { useSimpleSaveAction } from "@/controller/SaveContextProvider";
import { ClientManageDto, ClientStatus, ClientType } from "@/gen/api/user";
import { useUserTimeFormat } from "@/utils/userTimeFormat";
import retrieveAxiosErr from "@/utils/retrieveAxiosErr";
import { useSnackbar } from "notistack";


interface EditClientReviewProps {
  open: boolean;
  onClose: () => void;
}

const validationSchema =
  Yup.object().shape({
    ClientName: Yup.string().max(200).required('Client Name is required'),
    Remark: Yup.string().max(512),
  })

const EditClientReview: FC<EditClientReviewProps> = ({
  open,
  onClose
}) => {
  const match = useMediaQuery('(max-width: 530px)')
  const width = match ? 'calc(100vw - 50px)' : 480
  
  const save = useSimpleSaveAction()
  const record = useRecordContext<ClientManageDto>()
  const { enqueueSnackbar } = useSnackbar()

  const initialValues = {
    submit: '',
    status: null as ClientStatus|null,
    ClientName: record?.clientName || '',
    Remark: record?.remark || '',
  }

  const onSubmit: OnSubmit<typeof initialValues> = useCallback((values, {
    setStatus,
    setErrors,
    setSubmitting,
  }) => {
    const options = {
      onSuccess: () => {
        setStatus({ success: true })
        setSubmitting(false)
        onClose()
        if (values.status === ClientStatus.Enabled) {
          enqueueSnackbar(`Client "${values.ClientName}" is now active.`, { variant: 'success' })
        } else {
          enqueueSnackbar(`Rejection Operation succeed.`, { variant: 'success' })
        }
      },
      onFailure: (err: any) => {
        setStatus({ success: false })
        setErrors({ submit: err.message })
        setSubmitting(false)
        const errMsg = retrieveAxiosErr(err)
        if (values.status === ClientStatus.Enabled) {
          enqueueSnackbar(`Failed to enable Client "${values.ClientName}": ${errMsg}`, { variant: 'error' })
        } else {
          enqueueSnackbar(`Rejection Operation failed: ${errMsg}`, { variant: 'error' })
        }
      }
    }
    const payload = {
      status: values.status,
      prevStatus: ClientStatus.PendingApproval,
      ...(values.ClientName && values.ClientName !== record.clientName && {
        clientName: values.ClientName,
      }),
      ...((values.Remark ? values.Remark !== record.remark : !!record.remark) && {
        remark: values.Remark,
      }),
    }

    save(payload, options)
  }, [save, record, enqueueSnackbar, onClose])

  const formikCtrl = useFormik({ initialValues, onSubmit, validationSchema })
  const { errors, isSubmitting, handleSubmit, setFieldValue, resetForm } = formikCtrl

  const format = useUserTimeFormat()
  const dateVal = (record?.registerAt || '') as string
  const dateString = dateVal ? format(new Date(dateVal), 'Pp') : ''

  useEffect(() => {
    resetForm({
      values: {
        submit: '',
        status: null as ClientStatus|null,
        ClientName: record?.clientName || '',
        Remark: record?.remark || '',
      }
    })
  }, [record, resetForm])

  return (
    <Drawer
      anchor="right"
      // onOpen={() => {}}
      onClose={onClose}
      open={open}
      PaperProps={{
        sx: {
          p: 2,
          width: width,
        }
      }}
    >
      {record &&
        <OdForm formikCtrl={formikCtrl}>
          {open &&
            <Helmet>
              <title>Review Client Registration | OnData</title>
            </Helmet>}
          <Card>
            <CardHeader title="Review Client Registration" />
            <CardContent>
              <Grid
                container
                spacing={4}
              >
                <Grid item xs={12} >
                  <TextField label="Client Name*" name="ClientName" />
                </Grid>
                <Grid item xs={6} >
                  <Typography variant="body2">
                    {record.clientType === ClientType.DataDriver ? 'Data Driver'
                      : record.clientType === ClientType.EncryptionTool ? 'Encryption Tool'
                      : null
                    }
                  </Typography>
                  <Typography variant="caption" component="p" color="success.main">
                    {record.softwareVersion}
                  </Typography>
                </Grid>
                <Grid item xs={6} >
                  <Typography variant="body2">
                    {dateString}
                  </Typography>
                  <Tooltip title={record.r1Username || ''}>
                    <Box>
                      <Typography variant="caption">
                        Registered By <Typography variant="caption" color="primary.main">
                          {record.r1UserFirstName} {record.r1UserLastName}
                        </Typography>
                      </Typography>                
                    </Box>
                  </Tooltip>
                </Grid>
                <Grid item xs={6} >
                  <Typography variant="body2">
                    {record.osInfo}<br/>
                    <Typography variant="caption">
                      {record.ipAddress}
                    </Typography>
                  </Typography>
                </Grid>
                <Grid item xs={12} >
                  <TextField label="Remark" name="Remark" multiline={true} rows={4} />
                </Grid>
              </Grid>
              {errors.submit && (
                <Box sx={{ mt: 3 }}>
                  <FormHelperText error>
                    {errors.submit}
                  </FormHelperText>
                </Box>
              )}
            </CardContent>
            <Stack
              direction="row"
              justifyContent="space-between"
              sx={{ p: 2 }}
            >
              <Button
                color="error"
                disabled={isSubmitting}
                type="submit"
                variant="outlined"
                startIcon={<ClearIcon />}
                onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                  setFieldValue('status', ClientStatus.Rejected)
                  e.preventDefault()
                  setTimeout(() => handleSubmit())
                  return false
                }}
                >
                Reject
              </Button>
              <Button
                color="primary"
                disabled={isSubmitting}
                type="submit"
                variant="contained"
                startIcon={<CheckIcon />}
                onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                  setFieldValue('status', ClientStatus.Enabled)
                  e.preventDefault()
                  setTimeout(() => handleSubmit())
                  return false
                }}
              >
                Approve
              </Button>
            </Stack>
          </Card>

        </OdForm>
      }
    </Drawer>
  )
}

export default EditClientReview;
